import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTestContext } from '../../../context/testContext';
import { VisualFieldMatrixVF2 } from '../../../components';
import { client } from '../../../helpers/api-client';
import { useAppointment } from '../../../context/appointmentContext';
import { useResultData } from '../../../helpers/useResultData';
import { DataTable, SessionActions, ToggleGroup, useSessionUI, breakpoints } from '../../../ui';
import { StepBody, useConfirmLeave } from '../testScreenKit';

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto;
  gap: 24px;
  align-items: center;
  justify-items: center;
  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 16px;
  }
`;

// Step 4 - Visual Field results: eye switcher, summary table and the plotted field.
const VisualFieldResults = ({ localResults, setActiveTab, updateStep }) => {
  const [eyesTested, setEyesTested] = useState([]);
  const [currentEye, setCurrentEye] = useState();
  const [selectedOption, setSelectedOption] = useState();
  const [results, setResults] = useState({});
  const [remoteData, setRemoteData] = useState(null);
  const [localData, setLocalData] = useState(null);
  const { appointment } = useAppointment();
  const { updateTestData, testData } = useTestContext();
  const { confirm, toast } = useSessionUI();
  const confirmLeave = useConfirmLeave();
  const [saving, setSaving] = useState(false);
  const { clearAllResults } = useResultData();

  const getRemoteTestResults = useCallback(() => {
    const testId = testData?.testResultsId;
    client(`api/appointment-test-result/${testId}`).then(
      ({ result_data }) => {
        const allResults = result_data ? JSON.parse(result_data) : [];
        setRemoteData(allResults);
      },
      (error) => {
        console.error(error);
      },
    );
  }, [testData?.testResultsId]);

  useEffect(() => {
    if (!localResults) return getRemoteTestResults();
    if (localResults) return setLocalData(localResults);
  }, [localResults, getRemoteTestResults]);

  useEffect(() => {
    if (remoteData?.bothEyes || localData?.bothEyes) return setEyesTested(['Binocular']);
    if (remoteData?.rightEye || localData?.rightEye) setEyesTested((eyes) => [...eyes.filter((eye) => eye !== 'Right Eye'), 'Right Eye']);
    if (remoteData?.leftEye || localData?.leftEye) setEyesTested((eyes) => [...eyes.filter((eye) => eye !== 'Left Eye'), 'Left Eye']);
  }, [remoteData, localData]);

  useEffect(() => {
    const eyeLabelToKey = {
      'Left Eye': 'leftEye',
      'Right Eye': 'rightEye',
      Binocular: 'bothEyes',
    };

    setCurrentEye(eyeLabelToKey[selectedOption]);
  }, [selectedOption]);

  useEffect(() => {
    if (remoteData) return setResults(remoteData[currentEye]);
    if (localData) return setResults(localData[currentEye]);
  }, [remoteData, localData, currentEye]);

  const saveTestResults = async () => {
    const { corrected } = testData;
    let testResultData = { ...localResults, corrected };

    const sendTestResultData = {
      appointment_eye_test: testData.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify(testResultData),
    };

    if ((await confirm({ message: 'Are you sure you want to save the test results?' })) === true) {
      setSaving(true);
      client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
        () => {
          setSaving(false);
          updateTestData({ visualFieldTwoResults: {} });
          toast({ tone: 'success', title: 'Results saved', body: 'Visual Field results were added to this appointment.' });
          setActiveTab('results');
        },
        (error) => {
          setSaving(false);
          console.error('There was en error saving the test results - ', error);
          toast({ tone: 'error', title: 'Could not save the results', body: 'Please try again.' });
        },
      );
    }
  };

  const restartTest = () => {
    confirmLeave('restart').then(() => {
      clearAllResults();
      return updateStep('eyeSetup');
    });
  };

  const summary = [
    ['Fixation', results?.fixationType?.label || '--'],
    ['Response', 'Patient Mouse Click'],
    ['Greyscale', 'Maximum Contrast'],
    ['Target Duration', `${results?.targetDuration / 1000 || '--'}s`],
    ['Confidence Level', `${results?.testConfidence || 0}%`],
    ['Seen', `${results?.seenDots?.length || 0}`],
    ['Not Seen', `${results?.notSeenDots?.length || 0}`],
    ['Patient Distance', `${results?.patientDistance || '--'}mm`],
  ].map(([k, v]) => ({ id: k, k, v }));

  return (
    <StepBody gap={20} data-testid='vf-results'>
      <Header eyesTested={eyesTested} selectedOption={selectedOption} setSelectedOption={setSelectedOption} />
      <Columns>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <DataTable
            columns={[
              { key: 'k', label: 'Measure', align: 'left' },
              { key: 'v', label: 'Result', align: 'left' },
            ]}
            rows={summary}
          />
        </div>
        <VisualFieldMatrixVF2 testProgressData={results || {}} eyeTested={currentEye} results />
      </Columns>
      <SessionActions
        onBack={() => (localResults ? restartTest() : setActiveTab('results'))}
        backLabel={localResults ? 'Restart' : 'Back'}
        primary={localResults ? { label: saving ? 'Saving…' : 'Save Test Results', onClick: () => saveTestResults(), disabled: saving } : undefined}
      />
    </StepBody>
  );
};

export default VisualFieldResults;

const Header = ({ eyesTested, selectedOption, setSelectedOption }) => {
  useEffect(() => {
    if (eyesTested.includes('Binocular')) return setSelectedOption('Binocular');
    if (eyesTested.includes('Right Eye')) return setSelectedOption('Right Eye');
    return setSelectedOption('Left Eye');
  }, [eyesTested, setSelectedOption]);

  if (eyesTested.length < 2) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <ToggleGroup ariaLabel='Eye' options={eyesTested} value={selectedOption} onChange={setSelectedOption} />
    </div>
  );
};
